import React, { memo, useEffect, useState } from "react";
import { useFieldArray } from "react-hook-form";
import "react-phone-input-2/lib/style.css";
import swal from "sweetalert";
import { generateFields } from ".";
import { IDENTIFIER } from "../constants";
import {
  fetchDefaultValueForField,
  getValueByPath,
  isObjectNotArray,
} from "../utils";

const ChildForm = ({
  input,
  control,
  defaultValues,
  setValue,
  errors,
  getValues,
  parentName = "",
}) => {
  const { name, label, alignment = {}, isMoreRow, isDisabled = false, handleRemoveRow, isCheckable = false, isMultipleCheck = false, isConfirmation = false } = input;
  const { sm = 12, md = 12, lg = 12 } = alignment;

  const fullName = parentName ? `${parentName}.${name}` : name;

  const { fields, append, remove } = useFieldArray({
    control,
    name: fullName,
  });

  // State for managing selected rows
  const [selectedRows, setSelectedRows] = useState(new Set());

  const addRow = () => {
    const fieldsRows = input?.children?.reduce((acc, field) => {
      acc[field.name] = field.type && fetchDefaultValueForField(field.type);
      return acc;
    }, {});
    append({ ...fieldsRows });
  };

  const removeRow = (index) => {
    if (fields?.length > 1) {
      if (isConfirmation) {
        swal({
          title: "Are you sure you want to delete?",
          text: "",
          icon: "warning",
          buttons: true,
          dangerMode: true,
        }).then((willDelete) => {
          if (willDelete) {
            remove(index);
            if (handleRemoveRow) {
              handleRemoveRow(getValues(), setValue);
            }
          }
        });
      } else {
        remove(index);
        if (handleRemoveRow) {
          handleRemoveRow(getValues(), setValue);
        }
      }
    }
  };

  // Toggle row selection
  const toggleRowSelection = (index) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(index)) {
      newSelectedRows.delete(index);
    } else {
      if (!isMultipleCheck) {
        newSelectedRows.clear();
      }
      newSelectedRows.add(index);
    }
    setSelectedRows(newSelectedRows);
    const formValues = getValues(fullName);
    const updatedValues = formValues?.map((row, i) => {
      if (i === index) return { ...row, isActive: true }
      return { ...row, isActive: false }
    })
    setValue(fullName, updatedValues);
  };

  // Check if row is selected
  const isRowSelected = (index) => {
    return selectedRows.has(index);
  };

  useEffect(() => {
    const isNotDefaultValues = !defaultValues || Object.keys(defaultValues)?.length === 0;
    if (isNotDefaultValues && fields.length === 0) {
      addRow();
    }
  }, [defaultValues, fields]);

  // Set default selection for first row when there's only one row
  useEffect(() => {
    if (isCheckable) {
      if (defaultValues[fullName] && defaultValues[fullName]?.length > 0) {
        const activeIndexes = defaultValues[fullName]?.filter((row, i) => row.isActive).map((row, i) => i);
        if (activeIndexes?.length > 0) {
          setSelectedRows(new Set(activeIndexes));
        }
      }
    }
  }, [fields.length, selectedRows.size, defaultValues]);

  return (
    <div className={`form-group mb-3 col-sm-${sm} col-md-${md} col-lg-${lg}`}>
      <div style={{ marginBottom: "10px" }}>
        {label && <div className="sub-menu-title"> {label} </div>}
        {fields.map((rowFilter, index) => (
          <div
            key={rowFilter.id || index}
            className="row-wrapper"
            style={{
              marginTop: "5px",
              display: "flex",
              justifyContent: "space-around",
            }}
          >
            <div
              className="row"
              style={{
                border: "1px solid #e1d7d7",
                borderRadius: "10px",
                padding: "10px",
                marginBottom: "10px",
                marginLeft: 0,
                width: "100%",
                backgroundColor: isRowSelected(index) ? "#f8f9fa" : "transparent",
                borderColor: isRowSelected(index) ? "#007bff" : "#e1d7d7",
              }}
            >
              {input?.children?.map((row) => {
                const field = { ...row };
                const { name: childName, type, options } = field;
                const fullChildName = `${fullName}.${index}.${childName}`;
                const error = getValueByPath(errors, fullChildName + ".message");
                const defaultValue = rowFilter[childName] || "";

                if (field?.children?.length > 0 && field.isMoreRow) {
                  return (
                    <ChildForm
                      key={fullChildName}
                      input={field}
                      control={control}
                      defaultValues={defaultValues?.[name]?.[index]?.[childName] || {}}
                      setValue={setValue}
                      errors={errors}
                      getValues={getValues}
                      parentName={`${fullName}.${index}`}
                    />
                  );
                }
                if (type === IDENTIFIER.SELECT && isObjectNotArray(options)) {
                  const optionLists = options?.[index] || [];
                  field.options = optionLists;
                }

                if (type === IDENTIFIER.GROUPNUMBER && typeof field?.count === 'object') {
                  field.count = field.count[index];
                }

                return generateFields(
                  { ...field, name: fullChildName },
                  control,
                  error,
                  defaultValue,
                  setValue,
                  getValues,
                  field?.variant
                );
              })}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "5px", alignItems: "center" }}>
              {isMoreRow && !isDisabled && (
                <span
                  className="btn-icon-start-danger delete-button"
                  onClick={() => removeRow(index)}
                  style={{ margin: 0 }}
                  title="Delete Row"
                >
                  <i className="fa fa-trash color-info" />
                </span>
              )}
              {isCheckable && (
                <span
                  className={`btn-icon-start-${isRowSelected(index) ? 'success' : 'secondary'} select-button`}
                  onClick={() => toggleRowSelection(index)}
                  style={{
                    margin: 0,
                    position: 'relative',
                    top: '40%'
                  }}
                  title={isRowSelected(index) ? "Unselect Row" : "Select Row"}
                >
                  <i className={`fa ${isRowSelected(index) ? 'fa-check-circle' : 'fa-circle'} color-info`} />
                </span>
              )}
            </div>
          </div>
        ))}
        {isMoreRow && !isDisabled && (
          <div style={{ padding: "10px 10px 10px 0px" }}>
            <span className="btn-icon-start-danger ms-1 mt-1" onClick={addRow}>
              <i className="fa fa-plus color-info" />
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChildForm;
